import React from 'react';
import { formatDDMMYYYY, formatDDMonYYYY, formatHHMMSS } from '../../utils/dateI18n';
import { History, Clock, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { UserRole } from '../../types';

/**
 * Tabela de histórico de auditoria
 */
export default function TabelaHistoricoAuditoria({ logs, onDelete, currentUser }) {
  const isAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.OWNER;
  return (
    <div className="space-y-8 pt-12 border-t-4 border-slate-100">
      <div className="flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-slate-400">
            <History size={28}/>
          </div>
          <h3 className="text-2xl font-black text-slate-900 tracking-tight uppercase tracking-widest">
            Histórico de Auditoria
          </h3>
        </div>
        <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
          {logs.length} Registos Efetuados
        </span>
      </div>

      <div className="bg-white rounded-[3.5rem] shadow-2xl border-2 border-slate-50 overflow-hidden overflow-x-auto">
        <table className="w-full text-left min-w-[900px]">
          <thead className="bg-slate-50 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
            <tr>
              <th className="p-8">Timestamp</th>
              <th className="p-8">Dia de Venda</th>
              <th className="p-8 text-right">Teórico</th>
              <th className="p-8 text-right">Físico</th>
              <th className="p-8 text-center">Quebra/Sobra</th>
              <th className="p-8 text-center">Utilizador</th>
              {isAdmin && <th className="p-8 text-center">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {logs.slice().sort((a,b) => (b.closedAt || '').localeCompare(a.closedAt || '')).map(log => {
              const diff = (log.physicalValue || 0) - log.finalValue;
              const isOk = Math.abs(diff) < 0.01;
              const closedTime = log.closedAt ? formatHHMMSS(new Date(log.closedAt)) : "---";
              const closedDate = log.closedAt ? formatDDMMYYYY(new Date(log.closedAt)) : formatDDMMYYYY(new Date(log.date));
              
              return (
                <tr key={log.id} className="hover:bg-slate-50 transition-all duration-300 group">
                  <td className="p-8">
                    <div className="flex items-center gap-3">
                      <Clock size={16} className="text-green-600"/>
                      <span className="font-black text-slate-900 text-lg">{closedTime}</span>
                    </div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-1 px-7">{closedDate}</p>
                  </td>
                  <td className="p-8">
                    <span className="font-bold text-slate-600">
                      {formatDDMonYYYY(new Date(log.date))}
                    </span>
                  </td>
                  <td className="p-8 text-right font-bold text-slate-400">
                    {log.finalValue.toFixed(2)}€
                  </td>
                  <td className="p-8 text-right font-black text-slate-900 text-xl">
                    {(log.physicalValue || 0).toFixed(2)}€
                  </td>
                  <td className="p-8 text-center">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-black ${
                      isOk 
                        ? 'bg-emerald-50 border-emerald-100 text-emerald-600' 
                        : 'bg-orange-50 border-orange-100 text-orange-600'
                    }`}>
                      {isOk ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
                      {isOk ? 'OK' : `${diff.toFixed(2)}€`}
                    </div>
                  </td>
                  <td className="p-8 text-center">
                    <div className="flex flex-col items-center">
                      <span className="bg-slate-100 text-slate-700 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200 shadow-sm">
                        {log.closedBy}
                      </span>
                    </div>
                  </td>
                  {isAdmin && (
                    <td className="p-8 text-center">
                      <button
                        onClick={() => onDelete && onDelete(log.id)}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 border-2 border-red-200 hover:border-red-300 transition-all duration-200 shadow-sm hover:shadow-md group"
                        title="Eliminar registo"
                        aria-label="Eliminar registo de auditoria"
                      >
                        <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {logs.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="p-32 text-center text-slate-300 italic font-black text-2xl opacity-30">
                  Aguardando primeiro fecho de auditoria...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

